import { useState } from "react";
import { Loader2, Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { handleLogin } from "@/const";

const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: string;
}

export function LoginDialog({ open, onOpenChange, role }: LoginDialogProps) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const ok = await handleLogin(email);
    setSubmitting(false);
    if (ok) {
      onOpenChange(false);
      setEmail("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md p-0 gap-0 border-0"
        style={{
          background: MARINE_DEEP,
          border: `1px solid color-mix(in oklch, ${GOLD} 25%, transparent)`,
          boxShadow: `0 20px 60px color-mix(in oklch, ${GOLD} 12%, transparent)`,
        }}
      >
        <form onSubmit={onSubmit}>
          <DialogHeader className="px-8 pt-8 pb-4 space-y-3">
            <div
              className="w-12 h-12 flex items-center justify-center rounded-sm mb-2"
              style={{
                background: `color-mix(in oklch, ${GOLD} 15%, transparent)`,
                border: `1px solid color-mix(in oklch, ${GOLD} 40%, transparent)`,
              }}
            >
              <Mail size={20} style={{ color: GOLD }} />
            </div>
            <DialogTitle
              className="text-2xl text-left"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: "oklch(1 0 0)",
                letterSpacing: "0.01em",
              }}
            >
              {role ? `Accès ${role}` : "Accès Plateforme"}
            </DialogTitle>
            <DialogDescription
              className="text-left text-sm"
              style={{
                color: "oklch(0.85 0.01 243)",
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.5,
              }}
            >
              Entrez votre email professionnel. Un lien de connexion sécurisé vous sera envoyé.
            </DialogDescription>
          </DialogHeader>

          <div className="px-8 pb-2">
            <label
              htmlFor="login-email"
              className="block text-xs uppercase tracking-widest mb-2"
              style={{ color: GOLD, letterSpacing: "0.15em", fontFamily: "'Inter', sans-serif" }}
            >
              Email
            </label>
            <input
              id="login-email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              placeholder="prenom.nom@cabinet.com"
              className="w-full px-4 py-3 text-sm rounded-sm outline-none transition-colors"
              style={{
                background: "color-mix(in oklch, oklch(1 0 0) 4%, transparent)",
                border: `1px solid color-mix(in oklch, ${GOLD} 20%, transparent)`,
                color: "oklch(1 0 0)",
                fontFamily: "'Inter', sans-serif",
              }}
              onFocus={(e) =>
                (e.currentTarget.style.borderColor = `color-mix(in oklch, ${GOLD} 60%, transparent)`)
              }
              onBlur={(e) =>
                (e.currentTarget.style.borderColor = `color-mix(in oklch, ${GOLD} 20%, transparent)`)
              }
            />
          </div>

          <DialogFooter className="px-8 py-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="gsf-btn-outline"
              style={{ fontSize: "0.75rem", padding: "0.6rem 1.25rem" }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !email}
              className="gsf-btn-primary"
              style={{ fontSize: "0.75rem", padding: "0.6rem 1.5rem", opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Envoi…
                </>
              ) : (
                "Envoyer le lien"
              )}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
